import { useMemo } from "react";

import { EMPTY_PROFISSIONAIS_FILTERS } from "@/modules/recursos";
import { useProfissionais } from "@/screens/recursos/hooks/use-profissionais";

import {
  PROFISSIONAIS_LOAD_ERROR_MESSAGE,
  SUBSTITUTOS_PAGE_SIZE,
} from "../../resolver-conflito.constants";
import {
  envelopeConflito,
  formatIntervalo,
  obraTitulo,
  periodoObra,
  substitutoIndisponivel,
  toMonthRange,
} from "../../resolver-conflito.utils";
import type {
  SubstitutoStepProps,
  SubstitutoStepViewModel,
  SubstitutosEstado,
} from "./types-substituto-step";

/**
 * Etapa 1 do remanejamento: obra em que a substituição ocorrerá e o substituto,
 * buscado na listagem de profissionais (HU 1761) com a disponibilidade no
 * período da alocação.
 */
export function useSubstitutoStep({
  profissional,
  conflito,
  alocacaoId,
  substituto,
  search,
  errors,
  escolherObra,
  escolherSubstituto,
  buscar,
  limparBusca,
}: SubstitutoStepProps): SubstitutoStepViewModel {
  const alocacoes = [conflito.a, conflito.b];
  const alocacao = alocacoes.find((item) => item.id === alocacaoId) ?? null;
  const periodo = toMonthRange(alocacao ?? envelopeConflito(conflito));

  const result = useProfissionais({
    search,
    filters: EMPTY_PROFISSIONAIS_FILTERS,
    periodo,
    page: 1,
    pageSize: SUBSTITUTOS_PAGE_SIZE,
    alocacaoId: alocacaoId ?? undefined,
  });

  // O próprio profissional em conflito não pode substituir a si mesmo.
  const candidatos = useMemo(
    () => result.profissionais.filter((item) => item.id !== profissional.id),
    [result.profissionais, profissional.id],
  );

  let substitutos: SubstitutosEstado = { tipo: "carregando" };
  if (result.status === "error" || result.status === "forbidden") {
    substitutos = {
      tipo: "erro",
      mensagem:
        (result.status === "forbidden" ? result.errorMessage : null) ??
        PROFISSIONAIS_LOAD_ERROR_MESSAGE,
      recarregar: result.status === "error" ? result.reload : null,
    };
  } else if (result.status === "success") {
    if (candidatos.length > 0) {
      substitutos = {
        tipo: "lista",
        opcoes: candidatos.map((item) => ({
          id: item.id,
          nome: item.nome,
          papel: item.cargo || item.time || "-",
          indisponivel: substitutoIndisponivel(item),
          marcado: substituto?.id === item.id,
        })),
      };
    } else {
      substitutos = { tipo: search.trim() !== "" ? "semResultado" : "vazio" };
    }
  }

  return {
    profissionalNome: profissional.nome,
    profissionalTime: profissional.time,
    obras: alocacoes.map((item) => {
      const periodoDaObra = periodoObra(item.obra);
      return {
        id: item.id,
        titulo: obraTitulo(item.obra),
        periodoObra: periodoDaObra ? formatIntervalo(periodoDaObra) : "-",
        periodoAlocado: formatIntervalo(item),
        marcada: item.id === alocacaoId,
      };
    }),
    errors,
    search,
    substitutos,
    escolherObra,
    escolherSubstituto: (substitutoId) => {
      const escolhido = candidatos.find((item) => item.id === substitutoId);
      if (escolhido) escolherSubstituto(escolhido);
    },
    buscar,
    limparBusca,
  };
}
